/**
 * LinkModal — petite modale d'insertion/édition de lien.
 *
 * Affichée en overlay lorsque l'utilisateur clique sur le bouton Lien
 * de la toolbar. Capture une URL (et optionnellement un texte) puis
 * notifie le parent qui dispatch TOGGLE_LINK_COMMAND.
 */

import { useEffect, useRef, useState } from "react";

export interface LinkModalProps {
  open: boolean;
  initialUrl?: string;
  /** Texte sélectionné au moment de l'ouverture (lecture seule, info). */
  selectedText?: string;
  onConfirm: (url: string) => void;
  /** Si présent, un bouton "Retirer le lien" apparaît (cas où on édite un lien existant). */
  onRemove?: () => void;
  onCancel: () => void;
}

export function LinkModal({
  open,
  initialUrl = "",
  selectedText,
  onConfirm,
  onRemove,
  onCancel,
}: LinkModalProps) {
  const [url, setUrl] = useState(initialUrl);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset à chaque ouverture (sinon on garde la dernière URL de la session précédente)
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      // Focus différé pour laisser le navigateur peindre la modale d'abord
      const timer = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, initialUrl]);

  // Échap = annuler
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <div
      className="rte-link-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form className="rte-link-modal" onSubmit={handleSubmit}>
        <div className="rte-link-modal-title">
          {onRemove ? "Modifier le lien" : "Insérer un lien"}
        </div>
        {selectedText && (
          <div className="rte-link-modal-selected">
            Texte sélectionné : <strong>{selectedText}</strong>
          </div>
        )}
        <div className="rte-link-modal-field">
          <label htmlFor="rte-link-url">URL</label>
          <input
            id="rte-link-url"
            ref={inputRef}
            type="url"
            inputMode="url"
            placeholder="https://exemple.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="rte-link-modal-actions">
          {onRemove && (
            <button
              type="button"
              className="rte-link-modal-btn-danger"
              onClick={onRemove}
            >
              Retirer le lien
            </button>
          )}
          <button type="button" className="rte-link-modal-btn-ghost" onClick={onCancel}>
            Annuler
          </button>
          <button
            type="submit"
            className="rte-link-modal-btn-primary"
            disabled={!url.trim()}
          >
            {onRemove ? "Mettre à jour" : "Insérer"}
          </button>
        </div>
      </form>
    </div>
  );
}
